import React, { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Grid, Typography } from '@mui/material';
import { AxiosError } from 'axios';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import { response } from '../../../api/db';
import { registerUserInfo, userLogin } from '../../../api/db/user';
import {
  errorColor,
  gray100,
  primaryButtonBackgroundColor
} from '../../../constants/Colors';
import { headerHeight, spHeaderHeight } from '../../../constants/Location';
import { industryList } from '../../../constants/SelectOptions';
import { common } from '../../../modules/error';
import { userRegisterInfoSchema } from '../../../validation/user';
import type { UserRegisterInfoType } from '../../../validation/user/userType';

import CustomHalfTextfield from '../../uiParts/CustomHalfTextfield';
import CustomLoading from '../../uiParts/CustomLoading';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import CustomSelectField from '../../uiParts/CustomSelectField';
import CustomTextfield from '../../uiParts/CustomTextfield';
import Header from '../../uiParts/Header';
import PasswordTextfield from '../../uiParts/PasswordTextfield';
import CustomHead from '../CustomHead';
import { sentryLog } from '../../../config/sentry';

const styles = {
  button: {
    fontSize: '16px',
    fontWeight: '500',
    height: '44px',
    mt: '40px'
  },
  container: {
    m: `${headerHeight} auto 264px`,
    pt: '32px',
    width: '560px',
    '@media screen and (max-width:600px)': {
      m: `${spHeaderHeight} auto 204px`,
      pt: '24px',
      width: '90%'
    }
  },
  halfTextfieldWrapper: {
    '@media screen and (max-width:600px)': {
      width: '49%'
    }
  },
  label: {
    color: gray100,
    fontSize: '14px',
    mb: '12px'
  },
  labelRequired: {
    color: errorColor,
    fontSize: '14px',
    marginLeft: '8px'
  },
  title: {
    '@media screen and (max-width:600px)': {
      fontSize: '24px'
    },
    color: gray100,
    fontSize: '32px',
    fontWeight: '500',
    mb: '24px'
  }
};

const UserRegisterInfo: React.FC = () => {
  const location = useLocation();
  const userId = location.state?.id;
  const email = location.state?.email;
  const token = location.state?.token;
  const navigate = useNavigate();

  const [errorText, setErrorText] = useState('');
  const [firstSubmit, setFirstSubmit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
    trigger
  } = useForm<UserRegisterInfoType>({
    defaultValues: {
      industry: '選択してください'
    },
    resolver: yupResolver(userRegisterInfoSchema)
  });

  useEffect(() => {
    setValue('email', email);
  }, [email]);

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('company') === undefined ||
        '' ||
        watch('companyKana') === undefined ||
        '' ||
        watch('companyShort') === undefined ||
        '' ||
        watch('department') === undefined ||
        '' ||
        watch('email') === undefined ||
        '' ||
        watch('firstName') === undefined ||
        '' ||
        watch('firstNameKana') === undefined ||
        '' ||
        watch('industry') === undefined ||
        '' ||
        0 ||
        watch('lastName') === undefined ||
        '' ||
        watch('lastNameKana') === undefined ||
        '' ||
        watch('password') === undefined ||
        '' ||
        watch('passwordConfirm') === undefined ||
        '' ||
        watch('tel') === undefined ||
        '' ||
        Boolean(errors.company) ||
        Boolean(errors.companyKana) ||
        Boolean(errors.department) ||
        Boolean(errors.email) ||
        Boolean(errors.firstName) ||
        Boolean(errors.firstNameKana) ||
        Boolean(errors.homepage) ||
        Boolean(errors.industry) ||
        Boolean(errors.lastName) ||
        Boolean(errors.lastNameKana) ||
        Boolean(errors.password) ||
        Boolean(errors.passwordConfirm) ||
        Boolean(errors.root) ||
        Boolean(errors.tel)
      );
    }
    return false;
  };

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      await registerUserInfo(userId, email, token, watch);
      await userLogin(watch('email'), watch('password'));
      navigate('/auth/register/complete');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const loginErrorResponse = axiosError.response as response;
        setErrorText(loginErrorResponse.data.message);
        setOpen(true);
      } else {
        setErrorText(common);
        setOpen(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomHead title="会員情報登録 | CMCOS[コムコス]" />
      <CustomLoading loading={loading} />
      <Header />
      <Box sx={styles.container}>
        <Typography sx={styles.title}>会員登録</Typography>
        <form>
          <Controller
            render={({ field }) => (
              <CustomTextfield
                {...field}
                error={Boolean(errors.company)}
                formMb="24px"
                helperText={errors.company?.message}
                labelText="会社名"
                placeholder="株式会社○○○"
                requiredText="必須"
              />
            )}
            control={control}
            name="company"
          />
          <Controller
            render={({ field }) => (
              <CustomTextfield
                {...field}
                error={Boolean(errors.companyKana)}
                formMb="24px"
                helperText={errors.companyKana?.message}
                labelText="会社名(フリガナ)"
                placeholder="カブシキガイシャ○○○"
                requiredText="必須"
              />
            )}
            control={control}
            name="companyKana"
          />
          <Controller
            render={({ field }) => (
              <CustomTextfield
                {...field}
                error={Boolean(errors.companyShort)}
                formMb="24px"
                helperText={errors.companyShort?.message}
                labelText="会社名略称(7文字以内推奨)"
                placeholder="○○○"
                requiredText="必須"
              />
            )}
            control={control}
            name="companyShort"
          />
          <Controller
            render={({ field }) => (
              <CustomTextfield
                {...field}
                error={Boolean(errors.department)}
                formMb="24px"
                helperText={errors.department?.message}
                labelText="部署名"
                placeholder="○○部署"
                requiredText="必須"
              />
            )}
            control={control}
            name="department"
          />
          <Box sx={{ mb: '24px' }}>
            <Typography sx={styles.label}>
              担当者
              <Typography component="span" sx={styles.labelRequired}>
                必須
              </Typography>
            </Typography>
            <Grid container justifyContent="space-between">
              <Grid item sx={styles.halfTextfieldWrapper}>
                <Controller
                  render={({ field }) => (
                    <CustomHalfTextfield
                      {...field}
                      error={Boolean(errors.lastName)}
                      helperText={errors.lastName?.message}
                      labelText="姓"
                      placeholder="山田"
                    />
                  )}
                  control={control}
                  name="lastName"
                />
              </Grid>
              <Grid item sx={styles.halfTextfieldWrapper}>
                <Controller
                  render={({ field }) => (
                    <CustomHalfTextfield
                      {...field}
                      error={Boolean(errors.firstName)}
                      helperText={errors.firstName?.message}
                      labelText="名"
                      placeholder="太郎"
                    />
                  )}
                  control={control}
                  name="firstName"
                />
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ mb: '24px' }}>
            <Typography sx={styles.label}>
              担当者(フリガナ)
              <Typography component="span" sx={styles.labelRequired}>
                必須
              </Typography>
            </Typography>
            <Grid container justifyContent="space-between">
              <Grid item sx={styles.halfTextfieldWrapper}>
                <Controller
                  render={({ field }) => (
                    <CustomHalfTextfield
                      {...field}
                      error={Boolean(errors.lastNameKana)}
                      helperText={errors.lastNameKana?.message}
                      labelText="セイ"
                      placeholder="ヤマダ"
                    />
                  )}
                  control={control}
                  name="lastNameKana"
                />
              </Grid>
              <Grid item sx={styles.halfTextfieldWrapper}>
                <Controller
                  render={({ field }) => (
                    <CustomHalfTextfield
                      {...field}
                      error={Boolean(errors.firstNameKana)}
                      helperText={errors.firstNameKana?.message}
                      labelText="メイ"
                      placeholder="タロウ"
                    />
                  )}
                  control={control}
                  name="firstNameKana"
                />
              </Grid>
            </Grid>
          </Box>
          <Controller
            render={({ field }) => (
              <CustomTextfield
                {...field}
                error={Boolean(errors.tel)}
                formMb="24px"
                helperText={errors.tel?.message}
                labelText="電話番号"
                placeholder="000-0000-0000"
                requiredText="必須"
              />
            )}
            control={control}
            name="tel"
          />
          <Controller
            render={({ field }) => (
              <CustomTextfield
                {...field}
                error={Boolean(errors.email)}
                formMb="24px"
                helperText={errors.email?.message}
                labelText="メールアドレス"
                placeholder="sample@gmail.com"
                requiredText="必須"
                disabled={true}
              />
            )}
            control={control}
            name="email"
          />
          <Controller
            render={({ field }) => (
              <PasswordTextfield
                {...field}
                error={Boolean(errors.password)}
                formMb="24px"
                helperText={errors.password?.message}
                labelText="新しいパスワード"
                requiredText="必須"
                onChange={async (e) => {
                  field.onChange(e);
                  if (firstSubmit) {
                    await trigger('passwordConfirm');
                  }
                }}
              />
            )}
            control={control}
            name="password"
          />
          <Controller
            render={({ field }) => (
              <PasswordTextfield
                {...field}
                error={Boolean(errors.passwordConfirm)}
                formMb="24px"
                helperText={errors.passwordConfirm?.message}
                labelText="新しいパスワード(確認用)"
                requiredText="必須"
              />
            )}
            control={control}
            name="passwordConfirm"
          />
          <Controller
            render={({ field }) => (
              <CustomTextfield
                {...field}
                error={Boolean(errors.homepage)}
                formMb="24px"
                helperText={errors.homepage?.message}
                labelText="企業ホームページのURL"
                placeholder="https://xxx.com"
              />
            )}
            control={control}
            name="homepage"
          />
          <Controller
            render={({ field }) => (
              <CustomSelectField
                {...field}
                error={Boolean(errors.industry)}
                helperText={errors.industry?.message}
                labelText="業種"
                list={industryList}
                onChange={field.onChange}
                requiredText="必須"
                value={field.value}
              />
            )}
            control={control}
            name="industry"
          />
          <Button
            disabled={buttonDisabledFlag()}
            fullWidth
            onClick={() => {
              void (async () => {
                setFirstSubmit(true);
                await handleSubmit(async () => await onSubmit())();
              })();
            }}
            sx={[primaryButtonBackgroundColor, styles.button]}
            variant="contained"
          >
            登録する
          </Button>
        </form>
        <CustomSnackbar
          open={open}
          autoHideDuration={6000}
          onClose={() => setOpen(false)}
          text={errorText}
        />
      </Box>
    </>
  );
};

export default UserRegisterInfo;
